/**
 * @author Mohit Kukreja <mohit.kukreja@314ecorp.com>
 * @description Typography component that only shows tooltip when text is truncated
 */

import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { Tooltip, Typography, type TooltipProps } from 'antd';

interface ITypographyWithTooltipProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'content'> {
	content?: ReactNode;
	tooltipText?: string;
	showArrow?: boolean;
	tooltipProps?: TooltipProps;
	className?: string;
}

const COMMON_TYPOGRAPHY_WITH_TOOLTIP_STYLES = {
	// For tooltip elements that should shrink to give space to other elements
	tooltipShrinkable: 'shrink w-auto',
};

/**
 * Find the deepest element that contains text in the DOM tree
 */
const findDeepestTextContainer = (el: HTMLElement | null): HTMLElement | null => {
	if (!el) {
		return null;
	}

	// If this element directly contains a text node → this is the correct container
	for (const child of Array.from(el.childNodes)) {
		if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
			return el; // this element is the container of text
		}
	}

	// Otherwise, recurse into children that are elements
	for (const child of Array.from(el.children)) {
		const found = findDeepestTextContainer(child as HTMLElement);
		if (found) {
			return found;
		}
	}

	// No text container found
	return null;
};

/**
 * Extract plain text from content to show in tooltip
 */
const getTooltipText = (content?: ReactNode): string | undefined => {
	if (!content) {
		return undefined;
	}

	if (typeof content === 'string' || typeof content === 'number') {
		return String(content);
	}

	if (React.isValidElement(content)) {
		const childProps = content.props as { children?: ReactNode };
		if (typeof childProps.children === 'string' || typeof childProps.children === 'number') {
			return String(childProps.children);
		}
	}

	return undefined;
};

/**
 * TypographyWithTooltip
 * - Automatically detects truncation
 * - Shows tooltip only when truncated
 * - Auto-inherits width from parent
 */
const TypographyWithTooltip: React.FC<ITypographyWithTooltipProps> = ({
	content,
	tooltipText,
	tooltipProps,
	className = 'w-full',
	showArrow = false,
	...rest
}) => {
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		const el = textRef.current;
		const deepestNode = findDeepestTextContainer(el);
		if (!deepestNode) {
			return;
		}

		deepestNode.classList?.add('block', 'tw-ellipsis-text');
		const checkTruncation = () => {
			const truncated = deepestNode.scrollWidth > el.clientWidth;
			setIsTruncated(truncated);

			if (!truncated) {
				deepestNode.classList?.add('w-fit', 'pr-0.25');
			} else {
				deepestNode.classList?.remove('w-fit', 'pr-0.25');
			}
		};
		checkTruncation();

		const parent = el.parentElement;
		const resizeObserver = parent ? new ResizeObserver(checkTruncation) : undefined;
		if (parent && resizeObserver) {
			resizeObserver.observe(parent);
		}

		return () => resizeObserver?.disconnect();
	}, [content]);

	return (
		<Tooltip
			title={isTruncated ? tooltipText || getTooltipText(content) : undefined}
			placement='topLeft'
			arrow={showArrow}
			{...tooltipProps}
		>
			<Typography ref={textRef} className={`tw-ellipsis-text inline-block align-middle ${className}`} {...rest}>
				{content ?? '-'}
			</Typography>
		</Tooltip>
	);
};

export { COMMON_TYPOGRAPHY_WITH_TOOLTIP_STYLES };
export default TypographyWithTooltip;
